import { DndContext, DragOverlay, closestCenter } from "@dnd-kit/core"
import useAds from "@hooks/useAds"
import { useEffect, useState } from "react"
import DroppableContainer from "../../components/DroppableContainer/DroppableContainer.jsx"
import { useSelector } from "react-redux"
import { extractDriveId } from "@components/SortableItem/helper.jsx"
import { useNavigate } from "react-router"
import Navbar from "@components/Navbar/Navbar"
import useDefaultValues from "@hooks/useDefaultValues.js"
import useCreativeGrouping from "@hooks/useCreativeGrouping.js"
import { Loader } from "@components/Loader"
import { notify } from "@utils/notify"


// const userId = import.meta.env.VITE_USER_ID

const GroupCreativesPage = () => {
  const userId = localStorage.getItem("userId")
  const storedAdAccountId = localStorage.getItem("adAccountId");
  const storedAdAccountName = localStorage.getItem("adAccountName");

  const [rows, setRows] = useState({})
  const [activeId, setActiveId] = useState(null)
  const { ads } = useSelector((state) => state.ads)
  const [isSaving, setIsSaving] = useState(false)
  const [creativesMap, setCreativesMap] = useState({})
  const [ungroupedSets, setUngroupedSets] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  // console.log("ungroupedSets:", ungroupedSets)

  const navigate = useNavigate()
  const { handleGetAdsWithCreatives, handleUpdateCreativeGroups } = useAds()
  const { handleGetDefaultValues } = useDefaultValues()
  const { handleUngroupCreatives } = useCreativeGrouping()

  const { defaultValues } = useSelector((state) => state.defaultValues)

  useEffect(() => {
    setIsLoading(true)
    if (ads?.length) {
      const initialRows = ads.reduce((acc, ad) => {
        acc[ad._id] = ad.creatives.map((creative) => ({
          id: creative._id,
          fileName: creative.fileName,
          resolution: creative.resolution,
          fileLink: creative.fileLink,
          driveThumbnail: creative.driveThumbnail,
        }))
        return acc
      }, {})
      setRows(initialRows)
    }
    setIsLoading(false)
  }, [ads])

  useEffect(() => {
    setIsLoading(true)
    handleGetDefaultValues(userId, storedAdAccountId)
    setIsLoading(false)
  }, [userId, storedAdAccountId])

  useEffect(() => {
    setIsLoading(true)
    // Create mapping of creative IDs to their data
    const mapping = ads
      ?.flatMap((ad) =>
        ad.creatives.map((creative) => ({
          [creative._id]: creative,
        }))
      )
      .reduce((acc, item) => ({ ...acc, ...item }), {})

    setCreativesMap(mapping)
    setIsLoading(false)
  }, [ads])

  // Helper function to get creative data
  const getCreativeData = (id) => creativesMap?.[id] || null

  const handleDragStart = (event) => {
    setActiveId(event.active.id)
  }

  const handleDragEnd = (event) => {
    setActiveId(null)
    const { active, over } = event
    if (!over) return

    const activeContainer = active.data.current.sortable.containerId
    const overContainer = over.data.current?.sortable.containerId || over.id
    const activeCreativeId = active.id

     // Get the active creative's position type
    const activeCreative = getCreativeData(activeCreativeId)
    const activePositionType = activeCreative?.positionType
    // console.log("activePositionType:", activePositionType)

    // If dropping into a container (not reordering within the same container)
    if (activeContainer !== overContainer) {
      // Get all creatives in the target container
      const targetCreatives = [...(rows[overContainer] || [])]
      
      // Check if any creative in the target container has a different position_type
      const hasIncompatibleType = targetCreatives.some(creative => {
        const creativeData = getCreativeData(creative.id)
        return creativeData?.positionType === activePositionType
      })

      if (hasIncompatibleType) {
        notify("Cannot group creatives with same Placement type", "error")
        return
      }
    }

    const newRows = { ...rows }
    const sourceItems = [...newRows[activeContainer]]
    const [movedItem] = sourceItems.splice(
      active.data.current.sortable.index,
      1
    )
    const destItems = [...(newRows[overContainer] || [])]

    if (activeContainer === overContainer) {
      const activeIndex = active.data.current.sortable.index
      const overIndex = over.data.current?.sortable.index || 0

      const newRows = { ...rows }
      const items = [...newRows[activeContainer]]
      const [movedItem] = items.splice(activeIndex, 1)
      items.splice(overIndex, 0, movedItem)

      newRows[activeContainer] = items
      setRows(newRows)
    } else {
      destItems.push(movedItem)
      newRows[activeContainer] = sourceItems
      newRows[overContainer] = destItems
      setRows(newRows)
    }

    // Update ungrouped sets when combining creatives
    setUngroupedSets((prev) => {
      const updatedSets = [...prev]
      const activeSet = updatedSets.find((set) =>
        set.some((item) => item.id === activeCreativeId)
      )
      const targetSet = updatedSets.find((set) =>
        set.some((item) =>
          destItems.some((destItem) => destItem.id === item.id)
        )
      )

      if (activeSet && targetSet) {
        // Merge sets if both exist
        const mergedSet = [...new Set([...activeSet, ...targetSet])]
        return updatedSets
          .filter((set) => set !== activeSet && set !== targetSet)
          .concat([mergedSet])
      } else if (activeSet) {
        // Add target items to active set
        const newSet = [...activeSet, ...destItems]
        return updatedSets.filter((set) => set !== activeSet).concat([newSet])
      } else if (targetSet) {
        // Add active item to target set
        const newSet = [...targetSet, movedItem]
        return updatedSets.filter((set) => set !== targetSet).concat([newSet])
      }

      return prev
    })
  }

  useEffect(() => {
    setIsLoading(true)
    if (userId && defaultValues?.creativeLocationId)
      handleGetAdsWithCreatives(userId, defaultValues?.creativeLocationId)
    setIsLoading(false)
  }, [userId, defaultValues])

  const handleSaveAndNext = async () => {
    setIsSaving(true)
    try {
      const updates = Object.entries(rows)
        .filter(([_, creatives]) => creatives.length > 0)
        .map(([adId, creatives]) => ({
          adId,
          creativeIds: creatives.map((creative) => creative.id),
        }))

      await handleUpdateCreativeGroups(updates)

      const ungroupedSetsIds = ungroupedSets.map((set) =>
        set.map((creative) => creative.id)
      )
      if (ungroupedSets.length > 0) {
        await handleUngroupCreatives({
          ungrouped_creatives: ungroupedSetsIds,
          user_id: userId,
        })
      }
      navigate("/map-landing-page")
    } catch (error) {
      console.error("Failed to save creative groups:", error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleUngroup = (creativeId, sourceAdId) => {
    if (rows[sourceAdId].length <= 1) return

    const creative = rows[sourceAdId].find((item) => item.id === creativeId)
    const updatedSourceAd = rows[sourceAdId].filter(
      (item) => item.id !== creativeId
    )

    // Create new ad ID
    const newAdId = `new_ad_${Date.now()}`

    // Update rows
    setRows((prev) => ({
      ...prev,
      [sourceAdId]: updatedSourceAd,
      [newAdId]: [creative],
    }))

    // Add to ungrouped sets
    setUngroupedSets((prev) => {
      // Check if creative is already in any set
      const existingSetIndex = prev.findIndex((set) =>
        set.some((item) => item.id === creativeId)
      )

      if (existingSetIndex >= 0) {
        return prev
      }

      return [...prev, [creative]]
    })
  }

  return (
    <div className="max-w-5xl mx-auto flex flex-col gap-4 p-6 bg-white min-h-screen">
      <Navbar
        title="Group Creatives"
        onSaveAndNext={handleSaveAndNext}
        isSaving={isSaving}
        adAccountName={storedAdAccountName}
      />
      {(isLoading || isSaving) && (<div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50">
          <Loader />
        </div>)}
      <DndContext
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        collisionDetection={closestCenter}
      >
        <div className="grid grid-cols-2 gap-4">
          {Object.entries(rows)
            .filter(([_, items]) => items.length > 0) // Only show groups with items
            .map(([adId, items]) => {
              const adName =
                ads?.find((ad) => ad._id === adId)?.name || "New Ad"
              return (
                <DroppableContainer
                  key={adId}
                  id={adId}
                  items={items}
                  title={adName}
                  onUngroup={(creativeId) => handleUngroup(creativeId, adId)}
                />
              )
            })}
          <DragOverlay>
            {activeId ? (
              <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-xl cursor-grabbing w-[calc(100%-48px)] flex items-center gap-4 text-sm text-gray-700">
                <img
                  src={getCreativeData(activeId)?.driveThumbnail || `https://drive.google.com/thumbnail?sz=w256-h100&id=${extractDriveId(
                    getCreativeData(activeId)?.fileLink
                  )}`}
                  alt="Image from Google Drive"
                  className="max-w-36 max-h-36"
                />
                <div className="flex-1">
                  <div className="font-medium">{activeId.fileName}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {activeId.resolution}
                  </div>
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </div>
      </DndContext>
    </div>
  )
}

export default GroupCreativesPage
